import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { EmpresaProvider } from "@/context/EmpresaContext";
import { Toaster } from "sonner";
import NotaToastStack from "@/components/NotaToastStack";
import Layout from "@/components/Layout";
import { lazy, Suspense } from "react";
import PermissaoRota from "@/components/PermissaoRota";
import Login from "@/pages/Login";

// Páginas carregadas sob demanda: o navegador baixa só o código do módulo aberto
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Clientes = lazy(() => import("@/pages/Clientes"));
const Produtos = lazy(() => import("@/pages/Produtos"));
const Vendas = lazy(() => import("@/pages/Vendas"));
const NotasFiscais = lazy(() => import("@/pages/NotasFiscais"));
const Boletos = lazy(() => import("@/pages/Boletos"));
const ContasAReceber = lazy(() => import("@/pages/ContasAReceber"));
const Assinaturas = lazy(() => import("@/pages/Assinaturas"));
const EmailEnvios = lazy(() => import("@/pages/EmailEnvios"));
const DRE = lazy(() => import("@/pages/DRE"));
const Gestao = lazy(() => import("@/pages/Gestao"));
const Configuracoes = lazy(() => import("@/pages/Configuracoes"));
const Orcamentos = lazy(() => import("@/pages/Orcamentos"));
const OrcamentoForm = lazy(() => import("@/pages/OrcamentoForm"));
const TemplateEditor = lazy(() => import("@/pages/TemplateEditor"));

const Carregando = () => <div className="p-10 text-slate-500" data-testid="page-loading">Carregando…</div>;

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout><Suspense fallback={<Carregando />}>{children}</Suspense></Layout>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
        <EmpresaProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/clientes" element={<Protected><Clientes /></Protected>} />
            <Route path="/produtos" element={<Protected><PermissaoRota modulo="produtos"><Produtos tipo="produto" /></PermissaoRota></Protected>} />
            <Route path="/servicos" element={<Protected><PermissaoRota modulo="servicos"><Produtos tipo="servico" /></PermissaoRota></Protected>} />
            <Route path="/vendas" element={<Protected><PermissaoRota modulo="vendas"><Vendas tipo="venda" /></PermissaoRota></Protected>} />
            <Route path="/vendas-servicos" element={<Protected><PermissaoRota modulo="vendas-servicos"><Vendas tipo="servico" /></PermissaoRota></Protected>} />
            <Route path="/notas-fiscais" element={<Navigate to="/nfe" replace />} />
            <Route path="/nfe" element={<Protected><PermissaoRota modulo="nfe"><NotasFiscais tipo="NFE" /></PermissaoRota></Protected>} />
            <Route path="/nfse/importacao" element={<Navigate to="/nfse" replace />} />
            <Route path="/nfse" element={<Protected><PermissaoRota modulo="nfse"><NotasFiscais tipo="NFSE" /></PermissaoRota></Protected>} />
            <Route path="/boletos" element={<Protected><Boletos /></Protected>} />
            <Route path="/contas-a-receber" element={<Protected><ContasAReceber /></Protected>} />
            <Route path="/orcamentos" element={<Protected><Orcamentos /></Protected>} />
            <Route path="/orcamentos/novo" element={<Protected><OrcamentoForm /></Protected>} />
            <Route path="/orcamentos/:id/editar" element={<Protected><OrcamentoForm /></Protected>} />
            <Route path="/orcamentos/template/editor" element={<Protected><TemplateEditor /></Protected>} />
            <Route path="/emails" element={<Protected><EmailEnvios /></Protected>} />
            <Route path="/assinaturas" element={<Protected><PermissaoRota modulo="assinaturas"><Assinaturas /></PermissaoRota></Protected>} />
            <Route path="/financeiro" element={<Navigate to="/" replace />} />
            <Route path="/gestao" element={<Protected><Gestao /></Protected>} />
            <Route path="/dre" element={<Protected><DRE /></Protected>} />
            <Route path="/configuracoes" element={<Protected><Configuracoes /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" theme="light" richColors />
          {/* Stack customizado top-left só p/ notas — bypassa sonner */}
          <NotaToastStack />
        </EmpresaProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
